import type { DragEventArgs } from '@syncfusion/ej2-react-schedule'
import { Tooltip } from 'antd'
import type { TooltipRef } from 'antd/es/tooltip'
import {
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import { AwayTooltipLines, awayBandClassName } from './EventAwayOverlap'
import {
  type AwayDescription,
  type OverlapBand,
  describeAwayOverlap,
  getAwayOverlaps,
  overlapBands,
  visitSeconds,
  wallClockSeconds,
} from './awayOverlap'
import { resourceAwayStore } from './resourceAwayStore'

const CLONE_SELECTOR = '.e-schedule-event-clone'
const BAND_SELECTOR = '.event-away-overlap'
const ANCHOR_GAP_PX = 8
const ANCHOR_TOP_PX = 12

type Args = {
  containerRef: RefObject<HTMLDivElement | null>
  // The store key of the column under the drag: a resource id in Day view, a date in Week view.
  resourceFor: (args: DragEventArgs) => string | undefined
  instanceKey?: string
}

type DragAwayHint = {
  handleDrag: (args: DragEventArgs) => void
  clear: () => void
  hint: ReactNode
}

type DragAway = { lines: AwayDescription[]; bands: OverlapBand[] }

const NO_AWAY: DragAway = { lines: [], bands: [] }

// Drag arguments carry site wall-clock digits, so they are read without a zone.
const awayForDrag = (
  args: DragEventArgs,
  resourceFor: Args['resourceFor'],
  instanceKey?: string,
): DragAway => {
  const resourceId = resourceFor(args)
  const away = resourceId ? resourceAwayStore.get(instanceKey).get(resourceId) : undefined
  if (!away || !args.startTime || !args.endTime) return NO_AWAY

  const { start, end } = visitSeconds(
    wallClockSeconds(new Date(args.startTime)),
    wallClockSeconds(new Date(args.endTime)),
  )
  const overlaps = getAwayOverlaps(away.blocks, start, end)
  return {
    lines: overlaps.map((item) => describeAwayOverlap(item, away.staffLabel)),
    bands: overlapBands(overlaps, start, end),
  }
}

// The clone copies the card's bands from where it started; repaint them for where it is now.
const paintCloneBands = (clone: HTMLElement, bands: OverlapBand[]): void => {
  clone.querySelectorAll(BAND_SELECTOR).forEach((band) => band.remove())
  const host = clone.querySelector<HTMLElement>('.event-appointment-shell') ?? clone
  bands.forEach((band) => {
    const element = document.createElement('span')
    element.className = awayBandClassName(band)
    element.style.top = `${band.top}%`
    element.style.height = `${band.height}%`
    host.prepend(element)
  })
}

export const useDragAwayHint = ({ containerRef, resourceFor, instanceKey }: Args): DragAwayHint => {
  const anchorRef = useRef<HTMLSpanElement>(null)
  const tooltipRef = useRef<TooltipRef>(null)
  const linesKeyRef = useRef('')
  const [lines, setLines] = useState<AwayDescription[]>([])

  const showLines = useCallback((next: AwayDescription[]) => {
    const key = next.map((line) => `${line.title}|${line.interval}|${line.overlap}`).join('\n')
    if (key === linesKeyRef.current) return
    linesKeyRef.current = key
    setLines(next)
  }, [])

  const handleDrag = useCallback(
    (args: DragEventArgs) => {
      const { lines: nextLines, bands } = awayForDrag(args, resourceFor, instanceKey)
      const clone = containerRef.current?.querySelector<HTMLElement>(CLONE_SELECTOR)
      if (clone) {
        paintCloneBands(clone, bands)
        const anchor = anchorRef.current
        if (anchor) {
          const rect = clone.getBoundingClientRect()
          anchor.style.left = `${rect.right + ANCHOR_GAP_PX}px`
          anchor.style.top = `${rect.top + ANCHOR_TOP_PX}px`
        }
      }
      showLines(nextLines)
      tooltipRef.current?.forceAlign()
    },
    [containerRef, resourceFor, instanceKey, showLines],
  )

  const clear = useCallback(() => showLines([]), [showLines])

  // The popup mounts after the lines render; align it to the anchor once it exists.
  useEffect(() => {
    if (lines.length) tooltipRef.current?.forceAlign()
  }, [lines])

  const hint = (
    <Tooltip
      ref={tooltipRef}
      open={lines.length > 0}
      placement='right'
      title={<AwayTooltipLines lines={lines} />}>
      <span ref={anchorRef} className='schedule-away-drag-anchor' aria-hidden />
    </Tooltip>
  )

  return { handleDrag, clear, hint }
}
